import { useLayoutMeasure } from "@tellescope/react-hooks";
import { useTableCellContentWidth, useTableCellMaxContentHeight } from "./TableCellContext";

interface BadgeExtents {
  leadingWidth: number;
  widths: number[];
  labels: string[];
  badgeHeight: number;
  moreWidth: number;
  columnGap: number;
  rowGap: number;
}

/**
 * The measurement row holds an optional leading node (marked `data-badge-list-leading`), every badge,
 * then a worst-case "+ N more" badge.
 */
const readBadgeExtents = (row: HTMLElement): BadgeExtents => {
  const children = [...row.children].filter((child) => child instanceof HTMLElement);
  const leading = children.find((child) => child.hasAttribute("data-badge-list-leading"));
  const items = children.filter((child) => child !== leading);
  const badges = items.slice(0, -1);
  const { columnGap, rowGap } = getComputedStyle(row);
  return {
    leadingWidth: leading?.offsetWidth ?? 0,
    widths: badges.map((badge) => badge.offsetWidth),
    labels: badges.map((badge) => badge.textContent?.trim() ?? ""),
    badgeHeight: Math.max(0, ...items.map((item) => item.offsetHeight)),
    moreWidth: items.at(-1)?.offsetWidth ?? 0,
    columnGap: parseFloat(columnGap) || 0,
    rowGap: parseFloat(rowGap) || 0,
  };
};

const sameExtents = (a: BadgeExtents, b: BadgeExtents) =>
  a.leadingWidth === b.leadingWidth &&
  a.badgeHeight === b.badgeHeight &&
  a.moreWidth === b.moreWidth &&
  a.columnGap === b.columnGap &&
  a.rowGap === b.rowGap &&
  a.widths.length === b.widths.length &&
  a.widths.every((width, index) => width === b.widths[index] && a.labels[index] === b.labels[index]);

const readAvailableWidth = (element: HTMLElement) => element.clientWidth;

const sameWidth = (a: number, b: number) => a === b;

interface PackArgs {
  widths: number[];
  leadingWidth: number;
  gap: number;
  available: number;
}

/** Greedy flex-wrap packing: the line count the items take, and how wide the last line is. */
const pack = ({ widths, leadingWidth, gap, available }: PackArgs) =>
  widths.reduce(
    ({ lines, lineWidth }, width) =>
      lineWidth === 0
        ? { lines, lineWidth: width }
        : lineWidth + gap + width <= available
        ? { lines, lineWidth: lineWidth + gap + width }
        : { lines: lines + 1, lineWidth: width },
    { lines: 1, lineWidth: leadingWidth }
  );

/**
 * The most badges that fit in `maxLines` lines, reserving room for the "+ N more" badge on the last
 * line whenever any are left out. A single badge is always shown (clipped if it must be).
 */
const fitBadges = (extents: BadgeExtents, available: number, maxLines: number) => {
  const { widths, leadingWidth, columnGap: gap, moreWidth } = extents;
  const allFit =
    widths.every((width) => width <= available) && pack({ widths, leadingWidth, gap, available }).lines <= maxLines;
  if (widths.length === 1 || allFit) return widths.length;

  const counts = Array.from({ length: widths.length }, (_, index) => widths.length - 1 - index);
  const fitting = counts.find((count) => {
    const shown = widths.slice(0, count);
    // A badge wider than the column would only ever show clipped, so it doesn't count as fitting.
    if (shown.some((width) => width > available)) return false;
    const { lines, lineWidth } = pack({ widths: shown, leadingWidth, gap, available });
    const moreLines = lineWidth === 0 || lineWidth + gap + moreWidth <= available ? lines : lines + 1;
    return moreLines <= maxLines;
  });
  return fitting ?? 0;
};

export interface UseBadgeListFitOptions {
  /** Wrap across as many lines as a capped `expand` cell allows, instead of one line. */
  multiline?: boolean;
  /**
   * Identifies the current set of badges; the list re-measures after a commit only when it changes
   * (label changes that resize a badge still arrive through the observer).
   */
  badgesKey?: string;
}

/**
 * How many of `total` badges fit the list, leaving room for a "+ N more" badge when not all of them
 * do, and the text of the badges that don't. The list is one line, or with `multiline` as many lines
 * as a capped `expand` cell's height allows. Zero means not even one badge fits beside "+ N more" and
 * the list collapses to a summary; a single badge is always shown (clipped) instead.
 *
 * Inside a table the width budget is the column's, which the Table measures once for every cell, so a
 * column resize never wakes a per-cell observer. Only outside a table does the list measure its own
 * width (`boundsRef` is undefined inside one). Everything is measured before paint, so the list never
 * shows an unfitted frame on mount, remount or resize.
 */
export const useBadgeListFit = (total: number, { multiline = false, badgesKey }: UseBadgeListFitOptions = {}) => {
  const columnWidth = useTableCellContentWidth();
  const maxContentHeight = useTableCellMaxContentHeight();
  const { ref: ownBoundsRef, value: ownWidth } = useLayoutMeasure(readAvailableWidth, sameWidth, {
    commitKey: badgesKey,
  });
  const { ref: measureRef, value: extents } = useLayoutMeasure(readBadgeExtents, sameExtents, {
    commitKey: badgesKey,
  });
  const insideTable = columnWidth !== undefined;
  const available = insideTable ? columnWidth : ownWidth;

  const fitCount = (() => {
    if (available === undefined || extents === undefined || extents.widths.length !== total) return total;
    const maxLines =
      multiline && maxContentHeight !== undefined
        ? Math.max(1, Math.floor((maxContentHeight + extents.rowGap) / (extents.badgeHeight + extents.rowGap || 1)))
        : 1;
    return fitBadges(extents, available, maxLines);
  })();

  return {
    boundsRef: insideTable ? undefined : ownBoundsRef,
    measureRef,
    fitCount,
    hiddenLabels: extents?.labels.slice(fitCount) ?? [],
  };
};
